import React, { useEffect, useState } from 'react';
import { accent } from '../helpers';

/* ─────────────── Les deux effets, activés par `slide.effet` ───────────────
 * Ils partent tout seuls à l'affichage de la slide : aucune touche de plus sur
 * scène, la flèche droite reste la seule commande. Chacun est porté par un
 * sous-composant rendu SOUS le div à `key={slideKey}` — c'est ce qui les
 * remonte, et donc les rejoue, à chaque retour sur la slide. Des hooks posés
 * dans BigNumberSlide lui-même ne se réinitialiseraient pas. */

// « compteur » — slide des 200 appels / 200 jours.
const COMPTEUR_DEPART = 300;   // ms : laisse le fondu d'entrée commencer
const COMPTEUR_DUREE = 2200;   // ms
// La traduction attend que les deux compteurs soient arrivés : « Un appel. Un
// jour. » est la conclusion de l'égalité, elle ne peut pas s'afficher avant.
const COMPTEUR_FIN = (COMPTEUR_DEPART + COMPTEUR_DUREE) / 1000 + 0.3;

// « effondrement » — slide des 15 jours / 1 heure.
const EFFONDREMENT_DEPART = 2200;  // ms : le temps de lire « 15 jours » et sa barre
const EFFONDREMENT_DUREE = 1100;   // ms
const EFFONDREMENT_FIN = (EFFONDREMENT_DEPART + EFFONDREMENT_DUREE) / 1000;

// « repartition » — slide des 30 / 30 / 4 / 36.
const REPARTITION_DEPART = 1200;   // ms : les deux chiffres du haut d'abord
const REPARTITION_PAS = 560;       // ms entre deux segments
const REPARTITION_DUREE = 620;     // ms de croissance d'un segment

/**
 * Compte de 0 à la valeur affichée. Les deux chiffres d'une slide partent au
 * même instant, à la même vitesse, avec la même courbe : ils arrivent sur la
 * même image. C'est tout le propos — « l'égalité EST le message » (voir la
 * slide 19). Pas de décalage entre eux, donc pas de `stagger` en mode compteur.
 *
 * ⚠️ La valeur finale est posée INVISIBLE dans la même case de grille que le
 * compteur : elle réserve la largeur définitive. Sans elle, le chiffre passe
 * de 1 à 3 caractères pendant l'animation et les deux blocs centrés glissent
 * latéralement à chaque dizaine.
 * Une valeur qui ne commence pas par un entier est affichée telle quelle.
 */
const Compteur = ({ value }) => {
    const m = /^(\d+)([\s\S]*)$/.exec(value);
    const cible = m ? parseInt(m[1], 10) : null;
    const [n, setN] = useState(0);

    useEffect(() => {
        if (cible === null) return undefined;
        let raf;
        let t0 = null;
        const tick = (t) => {
            if (t0 === null) t0 = t;
            const p = Math.min(1, Math.max(0, (t - t0 - COMPTEUR_DEPART) / COMPTEUR_DUREE));
            // Décélération cubique : ça file, puis ça se pose sur la valeur.
            setN(Math.round(cible * (1 - Math.pow(1 - p, 3))));
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [cible]);

    if (cible === null) return value;
    return (
        <span className="inline-grid" style={{ fontVariantNumeric: 'tabular-nums' }}>
            <span className="invisible" style={{ gridArea: '1 / 1' }} aria-hidden="true">{value}</span>
            <span style={{ gridArea: '1 / 1' }}>{n}{m[2]}</span>
        </span>
    );
};

/**
 * La barre de la slide 28. Elle s'affiche pleine largeur sous « 15 jours »,
 * puis s'écrase jusqu'à la longueur d'« 1 heure » À LA MÊME ÉCHELLE — la même
 * leçon que la figure de la slide 4 : en linéaire, un petit chiffre est un
 * trait, et c'est le propos. La courbe accélère jusqu'au bout (ease-in) : ça
 * ne rétrécit pas, ça tombe.
 *
 * ⚠️ `echelle` = le rapport entre les deux durées. 360 = 15 jours de 24 h
 * rapportés à 1 heure, c'est-à-dire du DÉLAI calendaire, comme « 1 heure ». Si
 * les 15 jours sont des jours ouvrés de travail d'expert, le rapport honnête
 * est plutôt 15 × 7 = 105 : la barre finale serait trois fois plus longue, et
 * resterait un trait. À trancher avec l'équipe de la forge, pas à arrondir.
 * Le plancher de 4 px garde le trait visible du fond de la salle.
 */
const Effondrement = ({ echelle, gradient }) => {
    const [effondre, setEffondre] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setEffondre(true), EFFONDREMENT_DEPART);
        return () => clearTimeout(t);
    }, []);

    return (
        <div
            className="w-full animate-fade-in-up stagger-2"
            style={{ height: 'clamp(0.6rem, 2vh, 1.25rem)' }}
            aria-hidden="true"
        >
            <div
                className={`h-full rounded-full bg-gradient-to-r ${gradient}`}
                style={{
                    width: effondre ? `max(4px, ${100 / echelle}%)` : '100%',
                    transition: `width ${EFFONDREMENT_DUREE}ms cubic-bezier(0.7, 0, 0.84, 0)`,
                    // Halo neutre : la barre prend déjà la couleur du chiffre.
                    boxShadow: '0 0 24px rgba(255, 255, 255, 0.25)'
                }}
            />
        </div>
    );
};

/**
 * LA RÉPARTITION — les 100 % de la slide 24, segment par segment.
 *
 * POURQUOI ELLE EXISTE. La slide n'affichait que ses deux bons chiffres, et une
 * question de la salle le 21/09 a montré ce que ça produit : une partie du
 * public en est repartie en croyant que l'IA ne se trompait jamais. Le chiffre
 * qui manquait est celui des **réponses fausses affirmées avec assurance**. Il
 * est petit — 4 % — et c'est précisément pour ça qu'il doit être VU : une barre
 * où il n'occupe presque rien dit à la fois « c'est rare » et « ça existe »,
 * ce qu'aucune phrase ne fait aussi vite.
 *
 * TROIS PARTIS PRIS :
 *
 *  1. **Le total fait 100, et le total se montre.** Quatre chiffres posés côte
 *     à côte se discutent un par un ; une barre pleine ne se discute pas, elle
 *     se lit. C'est aussi ce qui rend le 4 % incontestable : il est à l'échelle.
 *  2. **Les segments arrivent dans l'ordre du discours**, un par un, et le
 *     4 % arrive en 3e — juste après les deux bons chiffres, avant le « je ne
 *     sais pas ». Adrien parle pendant que ça se construit ; l'écran ne doit
 *     jamais être en avance sur lui.
 *  3. **Aucune couleur nouvelle.** Le rouge du 4 % est celui du deck
 *     (`ACCENTS.red`), les deux bons segments sont le cyan des titres — le
 *     second en retrait plutôt qu'en teinte inventée — et le « je ne sais pas »
 *     est neutre, parce que ce n'est ni un succès ni une faute.
 *
 * ⚠️ `min-width` de 4 px sur chaque segment : sans lui, le 4 % disparaît sur un
 * écran étroit, et c'est exactement le chiffre qu'on est venu montrer.
 */
const TON_NEUTRE = { barre: 'bg-slate-600', texte: 'text-slate-400' };

/* ⚠️ UN SEGMENT ÉTROIT NE PORTE PAS DE DÉGRADÉ. Sur 4 % de la largeur, on ne
   voit qu'une tranche du milieu du dégradé : le rouge du deck
   (`from-red-400 via-orange-400`) s'affichait orange, c'est-à-dire pas rouge.
   En dessous de ce seuil, on peint donc le segment d'une seule couleur. */
const ETROIT = 10;   // en % de la barre
const PLEIN = { cyan: 'bg-cyan-400', red: 'bg-red-500', amber: 'bg-amber-400', violet: 'bg-violet-400' };

const Repartition = ({ segments }) => {
    const [arrives, setArrives] = useState(0);

    useEffect(() => {
        const minuteries = segments.map((_, i) => setTimeout(
            () => setArrives((n) => Math.max(n, i + 1)),
            REPARTITION_DEPART + i * REPARTITION_PAS
        ));
        return () => minuteries.forEach(clearTimeout);
    }, [segments]);

    const ton = (s) => {
        if (s.ton === 'neutre') return TON_NEUTRE;
        const barre = s.value < ETROIT
            ? (PLEIN[s.ton] || PLEIN.cyan)
            : `bg-gradient-to-r ${accent(s.ton).titre}`;
        return { barre, texte: accent(s.ton).texte };
    };

    return (
        <div className="w-full flex flex-col gap-[2.5vh]" aria-hidden="true">
            <div
                className="w-full flex gap-[3px] rounded-full overflow-hidden bg-white/5"
                style={{ height: 'clamp(0.8rem, 2.6vh, 1.6rem)' }}
            >
                {segments.map((s, i) => (
                    <div
                        key={i}
                        className={`h-full rounded-full ${ton(s).barre} ${s.retrait ? 'opacity-60' : ''}`}
                        style={{
                            // `flexBasis` plutôt que `width` : les segments se
                            // partagent la barre, les 3 px de jointure compris.
                            flexBasis: i < arrives ? `${s.value}%` : '0%',
                            minWidth: i < arrives ? '4px' : '0px',
                            transition: `flex-basis ${REPARTITION_DUREE}ms cubic-bezier(0.22, 1, 0.36, 1), min-width ${REPARTITION_DUREE}ms`
                        }}
                    />
                ))}
            </div>

            {/* La légende, et pas des étiquettes dans la barre : le 4 % n'a pas
                la place d'en porter une, et une seule étiquette déportée ferait
                du chiffre gênant une note de bas de page. */}
            <div className="w-full grid grid-cols-4 gap-[2vw] text-left">
                {segments.map((s, i) => (
                    <div
                        key={i}
                        className="flex flex-col gap-1"
                        style={{
                            opacity: i < arrives ? 1 : 0,
                            transform: i < arrives ? 'none' : 'translateY(0.4rem)',
                            transition: `opacity ${REPARTITION_DUREE}ms, transform ${REPARTITION_DUREE}ms`
                        }}
                    >
                        <p
                            className={`font-black leading-none ${ton(s).texte} ${s.retrait ? 'opacity-70' : ''}`}
                            style={{ fontSize: 'clamp(1.1rem, min(3vw, 5vh), 2.75rem)' }}
                        >
                            {s.value}&nbsp;%
                        </p>
                        <p
                            className="text-blue-200/60 font-light leading-tight"
                            style={{ fontSize: 'clamp(0.65rem, min(1.1vw, 2vh), 1.1rem)' }}
                        >
                            {s.label}
                        </p>
                    </div>
                ))}
            </div>
        </div>
    );
};

/**
 * Template "big-number" — un chiffre seul à l'écran, rien d'autre.
 *
 * Le conducteur l'exige explicitement pour les 200 jours et les résultats :
 * pas de tableau, pas de camembert, un chiffre par écran.
 *
 * ⚠️ Les tailles sont bornées par la HAUTEUR autant que par la largeur
 * (`min(vw, vh)`). Une taille en `rem` seule ne suffit pas : la page de
 * conférence met déjà le `font-size` racine à l'échelle de la largeur de
 * fenêtre, donc sur un écran large et court le chiffre débordait dans le
 * bandeau de chapitre et rognait la traduction. `whitespace-nowrap` garde
 * « 200 jours » sur une seule ligne — c'est un chiffre, pas une phrase.
 *
 * Champs de slide :
 *  - kicker      : petit label au-dessus du chiffre. Ajouté quand le cas nº 2
 *      a perdu sa slide d'ouverture : c'est désormais un chiffre qui ouvre le
 *      cas, et il lui fallait le même repère coloré que les deux autres.
 *  - figure      : le chiffre principal (ex. "200 jours")
 *  - figures     : [{ value, label }] pour afficher 2 chiffres côte à côte
 *  - translation : la phrase dite lentement juste après
 *  - note        : la précision d'honnêteté, en petit
 *  - accent      : "cyan" (défaut) | "red" | "amber" | "violet". Colore le
 *      chiffre et le kicker. Voir ACCENTS dans helpers.js.
 *  - effet       : optionnel, et réservé à deux slides du séminaire.
 *      "compteur"     — les chiffres comptent de 0 à leur valeur, ensemble ;
 *                       la traduction attend qu'ils soient arrivés.
 *      "effondrement" — une barre pleine largeur sous le premier chiffre
 *                       s'écrase à l'échelle du second, qui n'apparaît
 *                       qu'une fois la barre tombée. Lit `echelle`.
 *      "repartition"  — une barre de 100 % se construit segment par segment
 *                       sous les chiffres, avec sa légende. Lit `repartition`.
 *      Sans `effet`, la slide garde le simple fondu.
 *  - serre       : optionnel. Colle chaque libellé sous son chiffre. Retire la
 *      marge `pb-[0.1em]` qui protège les jambages : à réserver aux valeurs
 *      sans lettre descendante (« 30 % » oui, « 200 jours » non).
 *  - echelle     : pour "effondrement", le rapport entre les deux chiffres.
 *  - repartition : pour "repartition", [{ value, label, ton, retrait }] — la
 *      somme des `value` doit faire 100. `ton` : une clé d'ACCENTS, ou
 *      "neutre" ; `retrait` met le segment en retrait sans changer sa teinte.
 */
const BigNumberSlide = ({ slide, slideKey }) => {
    /* Palette sortie du fichier : trois templates avaient chacun leur table, et
       elles auraient fini par diverger. */
    const ton = accent(slide.accent);
    const gradient = ton.titre;
    const figures = slide.figures || (slide.figure ? [{ value: slide.figure }] : []);
    const solo = figures.length === 1;

    const figureSize = solo
        ? 'clamp(3rem, min(13vw, 30vh), 13rem)'
        : 'clamp(2.5rem, min(8vw, 18vh), 8rem)';

    const serre = !!slide.serre;
    const compteur = slide.effet === 'compteur';
    const effondrement = slide.effet === 'effondrement' && figures.length === 2;
    const repartition = slide.effet === 'repartition' && (slide.repartition || []).length > 0;
    // Le dernier segment arrivé met encore `REPARTITION_DUREE` à pousser.
    const repartitionFin = repartition
        ? (REPARTITION_DEPART + (slide.repartition.length - 1) * REPARTITION_PAS + REPARTITION_DUREE) / 1000
        : null;

    /* Quand un effet tient la scène, ce qui vient après lui (le second
       chiffre, la traduction, la note) attend qu'il ait fini : une phrase
       affichée pendant l'animation serait lue à sa place. */
    const finEffet = compteur ? COMPTEUR_FIN : effondrement ? EFFONDREMENT_FIN : repartitionFin;
    // `opacity: 0` inline : sans classe `stagger-*`, rien d'autre ne cache
    // l'élément pendant son délai, et il apparaîtrait puis rejouerait son fondu.
    const apresEffet = (extra) => (finEffet === null
        ? undefined
        : { animationDelay: `${finEffet + extra}s`, opacity: 0 });
    // Le second chiffre de l'effondrement est ce vers quoi la barre tombe : il
    // arrive avec elle, pas avant.
    const styleFigure = (i) => (effondrement && i === 1 ? apresEffet(0) : undefined);
    const classeFigure = (i) => (i > 0 && !slide.effet ? 'stagger-2' : '');

    return (
        <div key={slideKey} className="h-full flex flex-col items-center justify-center px-16 pb-16 text-center relative overflow-hidden">
            <div className="relative z-10 w-full max-w-6xl flex flex-col items-center gap-[4vh]">
                {slide.kicker && (
                    <p
                        className={`font-mono uppercase tracking-[0.3em] opacity-70 animate-fade-in-up ${ton.texte}`}
                        style={{ fontSize: 'clamp(0.7rem, min(1.3vw, 2.4vh), 1.5rem)' }}
                    >
                        {slide.kicker}
                    </p>
                )}

                <div className={`flex items-end justify-center flex-wrap ${solo ? '' : 'gap-[6vw]'}`}>
                    {figures.map((f, i) => (
                        <div key={i} className={`animate-fade-in-scale ${classeFigure(i)}`} style={styleFigure(i)}>
                            {/* `leading-none` + `bg-clip-text` coupait les jambes
                                descendantes : le dégradé ne peint que la boîte de
                                l'élément, et le « j » de « jours » en sortait. */}
                            <p
                                className={`font-black leading-[1.1] ${serre ? '' : 'pb-[0.1em]'} whitespace-nowrap bg-gradient-to-r ${gradient} bg-clip-text text-transparent`}
                                style={{ fontSize: figureSize }}
                            >
                                {compteur ? <Compteur value={f.value} /> : f.value}
                            </p>
                            {f.label && (
                                <p
                                    className={`${serre ? '' : 'mt-3'} text-blue-200/70 font-light`}
                                    style={{
                                        fontSize: 'clamp(0.9rem, min(1.6vw, 3vh), 1.75rem)',
                                        // Remonte le libellé dans le vide laissé sous
                                        // la ligne de base du chiffre, à l'échelle du
                                        // chiffre et non du libellé.
                                        ...(serre ? { marginTop: `calc(-0.12 * ${figureSize})` } : {})
                                    }}
                                >
                                    {f.label}
                                </p>
                            )}
                        </div>
                    ))}
                </div>

                {effondrement && <Effondrement echelle={slide.echelle || 360} gradient={gradient} />}

                {repartition && <Repartition segments={slide.repartition} />}

                {slide.translation && (
                    <p
                        className="text-slate-200 font-light leading-snug animate-fade-in-up stagger-3"
                        style={{ fontSize: 'clamp(1rem, min(2.2vw, 4vh), 2.25rem)', ...apresEffet(0.3) }}
                    >
                        {slide.translation}
                    </p>
                )}

                {slide.note && (
                    <p
                        className="text-blue-300/50 font-mono leading-snug animate-fade-in-up stagger-4"
                        style={{ fontSize: 'clamp(0.7rem, min(1.2vw, 2.2vh), 1.25rem)', ...apresEffet(0.5) }}
                    >
                        {slide.note}
                    </p>
                )}
            </div>
        </div>
    );
};

export default BigNumberSlide;
